import { useCallback, useEffect, useState } from 'react';
import { listOutbox, listQueuedPhotos } from './outbox.js';
import { flushOutbox, isOnline, watchConnectivity, type FlushReport } from './sync.js';
import { requestBackgroundSync } from './register-sw.js';

/**
 * How often, while QMS360 is open and online, waiting work is tried again
 * (Rule 1: a setting). The `online` event is the main trigger; this catches a
 * signal that came back without the browser noticing, and a server that was
 * briefly down.
 */
const RETRY_SECONDS = Number(import.meta.env.VITE_SYNC_RETRY_SECONDS ?? 60);

/**
 * Connectivity and queue state for the App 2 screens.
 *
 * The count is surfaced rather than hidden because on a site the interesting
 * question is never "am I online" — it is "has what I wrote actually left this
 * phone". A field engineer who cannot answer that will write it down twice, and
 * a silent queue is why.
 */

export interface OutboxState {
  online: boolean;
  /** Lessons and photos still waiting for a network. */
  pending: number;
  /** Entries the server has rejected often enough to need a person. */
  stuck: { id: string; error: string }[];
  flushing: boolean;
  lastReport: FlushReport | null;
  refresh: () => Promise<void>;
  flushNow: () => Promise<void>;
}

export function useOutbox(): OutboxState {
  const [online, setOnline] = useState(isOnline);
  const [pending, setPending] = useState(0);
  const [stuck, setStuck] = useState<{ id: string; error: string }[]>([]);
  const [flushing, setFlushing] = useState(false);
  const [lastReport, setLastReport] = useState<FlushReport | null>(null);

  const refresh = useCallback(async () => {
    const [entries, photos] = await Promise.all([listOutbox(), listQueuedPhotos()]);
    setPending(entries.length + photos.length);
  }, []);

  const flushNow = useCallback(async () => {
    setFlushing(true);
    try {
      const report = await flushOutbox();
      setLastReport(report);
      setStuck(report.stuck);
    } finally {
      setFlushing(false);
      await refresh();
    }
  }, [refresh]);

  useEffect(() => {
    void refresh();

    const goOnline = () => setOnline(true);
    const goOffline = () => setOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);

    const stopWatching = watchConnectivity((report) => {
      setLastReport(report);
      setStuck(report.stuck);
      void refresh();
    });

    // Try once on mount. A phone that was offline when the app was closed is
    // usually online by the time somebody opens it again.
    void flushOutbox().then((report) => {
      if (report.attempted > 0 || report.photosUploaded > 0) {
        setLastReport(report);
        setStuck(report.stuck);
      }
      void refresh();
    });

    // Background Sync asks an OPEN QMS360 tab to send when the browser sees a
    // signal return. It cannot send with no tab open: the sign-in and the merge
    // live in the page, by decision (FN-13) — waiting work sends while QMS360
    // is open with a signal, and the screens say so.
    void requestBackgroundSync();

    // And while open, try again on a timer.
    const timer = window.setInterval(() => {
      if (!isOnline()) return;
      void Promise.all([listOutbox(), listQueuedPhotos()]).then(([entries, photos]) => {
        if (entries.length + photos.length === 0) return;
        void flushOutbox().then((report) => {
          setLastReport(report);
          setStuck(report.stuck);
          void refresh();
        });
      });
    }, Math.max(RETRY_SECONDS, 10) * 1000);

    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
      window.clearInterval(timer);
      stopWatching();
    };
  }, [refresh]);

  return { online, pending, stuck, flushing, lastReport, refresh, flushNow };
}
